// CountdownTimer.jsx
import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

/** Keyframes para la animación del spinner */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/** Contenedor principal */
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** Agrupa los textos (fijo + dinámico) */
const Texts = styled.div`
  display: flex;
  flex-direction: column;
`;

/** Texto fijo + tiempo */
const Label = styled.span`
  font-size: 18px;
  font-weight: 500;
  padding-left: 12px;
  padding-right: 12px;
`;

/** Texto dinámico según lo que falte */
const DynamicText = styled.span`
  font-size: 14px;
  color: #555;
  margin-top: 4px;
`;

/** Spinner circular */
const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top-color: #027bc0;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

/** Helper: convierte "m,decimal" o "m" (minutos) a segundos */
const parseTimeStringToSeconds = (timeStr) =>
  Math.round(parseFloat(timeStr.replace(",", ".")) * 60);

/**
 * Props:
 * - text: etiqueta fija antes del tiempo
 * - timeString: cadena con minutos, p.ej. "1,5", "3", "4,5"
 * - onComplete: callback cuando llega a 0
 * - textFormatter: función(secondsLeft) → string dinámico
 */
export default function CountdownTimer({
  text,
  timeString,
  onComplete,
  textFormatter = () => "",
}) {
  // Estado interno de segundos
  const [secondsLeft, setSecondsLeft] = useState(
    parseTimeStringToSeconds(timeString)
  );

  // 1) Reiniciar si cambia timeString
  useEffect(() => {
    setSecondsLeft(parseTimeStringToSeconds(timeString));
  }, [timeString]);

  // 2) Cuenta atrás y reinicio automático al completar
  useEffect(() => {
    if (secondsLeft <= 0) {
      onComplete?.();
      // Reiniciar inmediatamente al valor de timeString
      setSecondsLeft(parseTimeStringToSeconds(timeString));
      return;
    }
    const timerId = setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [secondsLeft, onComplete, timeString]);

  // Formato mm:ss
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const formatted = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  const dynamicText = textFormatter(secondsLeft);

  return (
    <Container>
      <Texts>
        <Label>
          {text} {formatted}
        </Label>
        {dynamicText && <DynamicText>{dynamicText}</DynamicText>}
      </Texts>
      {/* Spinner si quieres mostrarlo mientras cuenta */}
      {/* {secondsLeft > 0 && <Spinner />} */}
    </Container>
  );
}
